<!-- 
Dialog for editing a levy estimator template.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog lg" >
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>
            {{ title }}
            <span class="body-1">{{ subtitle }}</span>
          </v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="levy-collection-setup" section="estimator-templates" />
        </v-toolbar>
        <our-busy-indicator :show="busy"/>
        <v-card-text>
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <v-flex xs12 sm12>
                <mavon-editor
                  v-show="show && !loading"
                  :value="template && template.template ? template.template : ''"
                  :editable="canEdit"
                  :toolbars="toolbars"
                  :toolbars-flag="canEdit"
                  :box-shadow="false"
                  :rules="[rules.required]"
                  font-size="12px"
                  language="en"
                  style="height: 306px"
                  @change="update($event)"
                />
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <template v-if="canEdit">
            <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
            <v-btn
              ref="save-button" 
              :disabled="busy" 
              flat 
              color="success"
              type="submit" 
              @click.prevent="save"
            >
              Save
            </v-btn>
          </template>
          <v-btn v-else flat @click.native="hide">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error" />
  </v-dialog>
</template>

<script>
import filterMixins from "@/mixins/filter.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import mavonMixins from "@/mixins/mavon.mixins";
import * as claim from "@/store/claim-types";
import * as action from "@/store/estimator-templates/action-types";
import * as mutation from "@/store/estimator-templates/mutation-types";

export default {
  mixins: [ruleMixins, lookupMixins, filterMixins, mavonMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    levyCollectionId: {
      required: true,
      type: Number
    },
    type: {
      required: true,
      validator: prop => typeof prop === "string" || prop === null
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showValidationDialog: false
    };
  },
  computed: {
    levyCollection() {
      return this.$store.state.levyCollections.view.data;
    },
    levyCollectionDescription() {
      return `${this.levyCollection.year} ${this.lookups.levyCollectionTypeDescriptions[this.levyCollection.type]}`;
    },
    typeDescription() {
      return this.type ? this.lookups.estimatorTemplateTypeDescriptions[this.type] : "";
    },
    state() {
      return this.$store.state.estimatorTemplates.edit;
    },
    template() {
      return this.state.data;
    },
    busy() {
      return this.state.loading || this.state.saving;
    },
    loading() {
      return this.state.loading;
    },
    title() {
      if (this.loading) {
        return "Estimator template";
      } else if (this.canEdit) {
        return `Edit "${this.typeDescription}" estimator template`;
      } else {
        return `"${this.typeDescription}" estimator template`;
      }
    },
    subtitle() {
      return !this.loading ? this.levyCollectionDescription : "";
    },
    canEdit() {
      const hasPermission = this.$store.getters.claims.includes(claim.ESTIMATOR_TEMPLATES_EDIT);
      const status = this.$store.state.levyCollections.view.data.status;
      const isAllowableStatus = this.doesLookupHaveAttribute(
        status,
        "CanEditEstimatorTemplates",
        this.lookups.levyCollectionStatuses
      );
      return hasPermission && isAllowableStatus;
    }
  },
  watch: {
    show() {
      this.load();
    },
    type() {
      this.load();
    },
    loading() {
      if (!this.loading) {
        this.$nextTick(function() {
          var textArea = document.getElementsByClassName("auto-textarea-input")[0];
          textArea.setSelectionRange(0, 0);
          textArea.focus();
        });
      }
    }
  },
  methods: {
    load() {
      if (this.show && this.type) {
        this.$store.dispatch(action.EDIT_GET, { type: this.type, levyCollectionId: this.levyCollectionId });
      }
    },
    update(template) {
      this.$store.commit(mutation.EDIT_SET_DATA, template);
    },
    async save() {
      if (this.$refs.form.validate() && (await this.$store.dispatch(action.EDIT_SAVE))) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>

<style lang="scss">
.notice-template-body {
  font-size: 12px;
  font-family: monospace;
}
</style>
